import { useI18n } from 'vue-i18n';
import { setI18nLanguage, type MessageLanguages } from '@plugins/i18n';
import { useLocaleStore } from '@/stores/localeStore';
import { LocaleEnum } from '@/types/enums/LocaleEnum';

const CZECH: MessageLanguages = LocaleEnum.Czech;

const STATIC_LANGUAGES = [
    { value: LocaleEnum.Czech, name: 'Čeština', flag: '🇨🇿' },
    { value: LocaleEnum.English, name: 'English', flag: '🇬🇧' },
    { value: LocaleEnum.Ukrainian, name: 'Українська', flag: '🇺🇦' },
];

// Languages offered for translatable content (product names, categories, ...).
// Ukrainian is a UI-only language, so it gets no content tab.
const CONTENT_LANGUAGE_CODES: MessageLanguages[] = [LocaleEnum.Czech, LocaleEnum.English];
const CONTENT_LANGUAGES = STATIC_LANGUAGES.filter(lang => CONTENT_LANGUAGE_CODES.includes(lang.value));

export function useTranslation() {
    const { t, locale, availableLocales } = useI18n();
    const localeStore = useLocaleStore();

    const changeLanguage = async (lang: MessageLanguages): Promise<void> => {
        await setI18nLanguage(lang, true);
    };

    const initializeLocale = () => {
        const savedLocale = localStorage.getItem('buylo-locale');
        if (savedLocale && availableLocales.includes(savedLocale)) {
            locale.value = savedLocale;
        } else {
            locale.value = CZECH;
        }
    };

    const getLanguages = () => STATIC_LANGUAGES;

    const getContentLanguages = () => CONTENT_LANGUAGES;

    const getCurrencies = () =>
        localeStore.currencies.map(curr => ({
            value: curr.code,
            name: curr.code,
            code: curr.code,
        }));

    const fetchLanguages = async (force = false): Promise<void> => {
        await localeStore.fetchLanguages(force);
    };

    const fetchCurrencies = async (force = false): Promise<void> => {
        await localeStore.fetchCurrencies(force);
    };

    const fetchLocaleData = async (force = false): Promise<void> => {
        await localeStore.fetchAll(force);
    };

    return {
        t,
        locale: locale.value as MessageLanguages,
        localeRef: locale,
        availableLocales,
        changeLanguage,
        initializeLocale,
        getLanguages,
        getContentLanguages,
        getCurrencies,
        fetchLanguages,
        fetchCurrencies,
        fetchLocaleData,
    };
}
